import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { signIn, useSession } from "../lib/auth-client";
import { Icon } from "../components/Common.jsx";
import AuthSide from "../components/AuthSide.jsx";
import AuthThemeToggle from "../components/AuthThemeToggle.jsx";
import PeekMascot from "../components/PeekMascot.jsx";

const VERIFIED_BANNERS = {
  1: { tone: "ok", text: "Email verified. Sign in to continue." },
  invalid: { tone: "err", text: "That verification link is invalid." },
  expired: {
    tone: "err",
    text: "That verification link has expired. Please sign up again.",
  },
  error: {
    tone: "err",
    text: "Something went wrong verifying your email. Please try again.",
  },
};

const REGISTERED_BANNER = {
  tone: "ok",
  text: "You're registered. Sign in to continue.",
};

export default function SigninPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const verifiedBanner =
    VERIFIED_BANNERS[params.get("verified")] ||
    (params.get("registered") === "1" ? REGISTERED_BANNER : undefined) ||
    (params.get("reset") === "1"
      ? { tone: "ok", text: "Password updated. Sign in with your new password." }
      : undefined);

  const { data: session } = useSession();
  // "Add account" flow: a user who is already signed in lands here to add a
  // second account (multi-session). Skip the auto-redirect so they can reach
  // the form; signing in keeps the previous account available to switch back.
  const addAccountMode = params.get("addAccount") === "1";

  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [passFocused, setPassFocused] = useState(false);

  useEffect(() => {
    if (addAccountMode) return; // adding another account — stay on the form
    if (session?.session) {
      const role = session.user?.role;
      if (role === "admin") {
        navigate("/app?page=adminOverview", { replace: true });
      } else {
        navigate("/app", { replace: true });
      }
    }
  }, [session, navigate, addAccountMode]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    const { data, error: authError } = await signIn.email({
      email: email.trim(),
      password: pass,
      rememberMe: true,
    });

    setSubmitting(false);

    if (authError) {
      const code = authError.code || authError.status;
      if (
        code === "EMAIL_NOT_VERIFIED" ||
        /verif/i.test(authError.message || "")
      ) {
        setError(
          "Please verify your email first. Check your inbox for the link.",
        );
      } else {
        setError(authError.message || "Invalid email or password.");
      }
      return;
    }

    if (data?.user?.role === "admin") {
      navigate("/app?page=adminOverview", { replace: true });
    } else {
      navigate("/app", { replace: true });
    }
  };

  return (
    <div className="auth-page">
      <AuthThemeToggle />
      <AuthSide
        title="Daily reports that write themselves."
        subtitle="Your team submits via WhatsApp in 30 seconds. Our AI structures everything into beautiful dashboards."
        stats={[
          { v: "10K+", l: "Active users" },
          { v: "2.4M", l: "Reports processed" },
          { v: "99.9%", l: "Uptime" },
        ]}
        chatVariant="A"
      />
      <div className="auth-form-wrap">
        <form className="auth-form" onSubmit={submit}>
          <PeekMascot covered={passFocused} />
          <h1>{addAccountMode ? "Add another account" : "Welcome back"}</h1>
          <p>
            {addAccountMode
              ? "Sign in to a second account — your current one stays signed in, and you can switch between them anytime."
              : "Sign in to your ReportFlow account"}
          </p>

          {verifiedBanner && (
            <div
              role="status"
              style={{
                marginBottom: 16,
                padding: "10px 14px",
                borderRadius: 10,
                fontSize: 13,
                border: "1px solid",
                background:
                  verifiedBanner.tone === "ok"
                    ? "rgba(16,185,129,0.1)"
                    : "rgba(239,68,68,0.1)",
                borderColor:
                  verifiedBanner.tone === "ok"
                    ? "rgba(16,185,129,0.4)"
                    : "rgba(239,68,68,0.4)",
                color: verifiedBanner.tone === "ok" ? "#10B981" : "#F87171",
              }}
            >
              {verifiedBanner.text}
            </div>
          )}

          {error && (
            <div
              role="alert"
              style={{
                marginBottom: 16,
                padding: "10px 14px",
                borderRadius: 10,
                fontSize: 13,
                background: "rgba(239,68,68,0.1)",
                border: "1px solid rgba(239,68,68,0.4)",
                color: "#F87171",
              }}
            >
              {error}
            </div>
          )}

          <button
            type="button"
            className="btn btn-ghost"
            onClick={() =>
              signIn.social({
                provider: "google",
                callbackURL: `${window.location.origin}/complete-signup`,
              })
            }
            style={{
              width: "100%",
              justifyContent: "center",
              gap: 10,
              marginBottom: 16,
            }}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 18,
            color: "var(--text-muted)",
            fontSize: 12,
          }}>
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
            <span>or sign in with email</span>
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
          </div>

          <div className="field">
            <label>Email address</label>
            <div className="input-group">
              <Icon name="mail" size={16} />
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
          </div>

          <div className="field">
            <label>Password</label>
            <div className="input-group">
              <Icon name="lock" size={16} />
              <input
                type={showPass ? "text" : "password"}
                className="input"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                onFocus={() => setPassFocused(true)}
                onBlur={() => setPassFocused(false)}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="input-suffix"
                onClick={() => setShowPass((s) => !s)}
              >
                <Icon name={showPass ? "eye-off" : "eye"} size={16} />
              </button>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 18,
            }}
          >
            <label className="checkbox">
              <input type="checkbox" defaultChecked /> Remember me
            </label>
            <Link
              to="/forgot-password"
              style={{
                color: "var(--primary-2)",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Forgot password?
            </Link>
          </div>

          <button
            className="btn btn-primary"
            type="submit"
            disabled={submitting}
            style={{ width: "100%", justifyContent: "center" }}
          >
            {submitting ? (
              "Signing in..."
            ) : (
              <>
                Sign in <Icon name="arrow-right" size={16} />
              </>
            )}
          </button>

          <div className="auth-foot">
            New to ReportFlow? <Link to="/signup">Create an account</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
